// Keeps a signed-in account's saved state in step with this device.

import { refreshMembership } from './membershipSync';
import { AccountError, accountService, type Account } from '../lib/account';
import { durable, validate } from './persistence';
import { useStore } from './store';
import { mergeStates } from './sync';

let pushTimer: ReturnType<typeof setTimeout> | undefined;
let unsubscribe: (() => void) | null = null;
/** The state last sent to (or merged from) the account, so unchanged state isn't re-sent. */
let last = '';
let lastSync = 0;
const MAX_TRIES = 3;

/**
 * Bring this device and the account together: pull, merge (nothing is lost, safety rules are a
 * union), save. Saves are compare-and-swap on the account's revision, so if another device saved
 * in between, this pulls and merges again instead of overwriting it.
 */
async function sync(): Promise<void> {
  const service = accountService();
  if (!service) return;
  for (let i = 0; i < MAX_TRIES; i++) {
    const remote = validate(await service.pull());
    const merged = mergeStates(durable(useStore.getState()), remote);
    last = JSON.stringify(merged);
    useStore.getState().replaceDurable(merged);
    if ((await service.push(merged)) === 'saved') { lastSync = Date.now(); return; }
  }
  throw new AccountError('Couldn’t save to your account. Try again.');
}

/** Save a change; if another device saved first, merge with it rather than overwrite it. */
async function save(): Promise<void> {
  const service = accountService();
  if (!service) return;
  const state = durable(useStore.getState());
  last = JSON.stringify(state);
  if ((await service.push(state)) === 'conflict') await sync();
}

/** Save changes to the account a moment after they happen, and catch up when the app comes back. */
function watch() {
  unsubscribe?.();
  last = JSON.stringify(durable(useStore.getState()));
  const stopStore = useStore.subscribe((s) => {
    if (!s.account) return;
    if (JSON.stringify(durable(s)) === last) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(() => { void save().catch(() => { /* retried on next change */ }); }, 1500);
  });
  // Another device may have saved while this one was in the background.
  const onVisible = () => {
    if (document.visibilityState !== 'visible' || !useStore.getState().account || Date.now() - lastSync < 30_000) return;
    void sync().catch(() => { /* offline: the next change or return tries again */ });
  };
  document.addEventListener('visibilitychange', onVisible);
  unsubscribe = () => { stopStore(); document.removeEventListener('visibilitychange', onVisible); };
}

/**
 * A failed web sign-in (Apple) comes back as `?error_description=…` and `#error_description=…`.
 * Say so once, then tidy the address so a reload or share doesn't carry the error along.
 */
function reportSignInError() {
  const { search, hash, pathname } = window.location;
  const params = [new URLSearchParams(search), new URLSearchParams(hash.slice(1))];
  const described = params.find((p) => p.has('error_description'));
  if (!described) return;
  useStore.getState().showToast('Sign-in didn’t work. Please try again, or use your email.');
  const keep = new URLSearchParams(search);
  for (const k of ['error', 'error_code', 'error_description', 'sb']) keep.delete(k);
  history.replaceState(history.state, '', `${pathname}${keep.size ? `?${keep}` : ''}`);
}

/** On app start: restore the signed-in account, if any, and bring its state in. */
export async function initAccount(): Promise<void> {
  reportSignInError();
  void refreshMembership();
  const service = accountService();
  if (!service) { useStore.getState().setAccountChecked(); return; }
  try {
    const account = await service.current();
    if (account) useStore.getState().setAccount(account);
    useStore.getState().setAccountChecked();
    if (!account) return;
    // Watch first: saves are compare-and-swap, so even if this first sync fails (offline), a later
    // save can't overwrite the account; it merges with it instead.
    watch();
    await sync();
  } catch {
    // Offline or unavailable: the app keeps working on this device.
  } finally {
    useStore.getState().setAccountChecked();
  }
}

/** After a code is verified: merge this device with the account, then run what was waiting. */
export async function completeSignIn(account: Account): Promise<void> {
  const s = useStore.getState();
  s.setAccount(account);
  watch();
  await sync();
  void refreshMembership();
  const pending = useStore.getState().accountPrompt;
  useStore.getState().closeAccountPrompt();
  pending?.run();
}

/** Sign out: save to the account, then clear this device so the next person starts fresh. */
export async function signOut(): Promise<void> {
  const service = accountService();
  if (!service) return;
  clearTimeout(pushTimer);
  try { await save(); } catch { /* best effort */ }
  await service.signOut();
  unsubscribe?.();
  unsubscribe = null;
  useStore.getState().wipeAll();
}
